import classNames from "classnames"
import React from "react"

import IconCell from "./IconCell"
import {
  extractFromTag,
  getIconFillClass,
  getThemeColor,
  getCheckmarkStrokeColor,
  getDashStrokeColor,
  parseContent,
  extractPriceData,
  matchCellPatterns,
} from "./utils"
import { Color } from "../../../../constants/V2/color"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import CircleCheckIcon from "../../../elements/V2/Icons/CircleCheckIcon"
import CircleCrossIcon from "../../../elements/V2/Icons/CircleCrossIcon"
import Logo from "../../../elements/V2/Icons/YocoLogoIcon"
import Tooltip from "../../../elements/V2/Tooltip"
import Typography from "../../../elements/V2/Typography"

import CircleDashIcon from "@components/elements/V2/Icons/CircleDashIcon"
import { useGetProductBySlug } from "@hooks/V2/storyblok/useProduct"
import { checkPromotion } from "@utils/shop"
import { getAccentColor } from "@utils/V2/color"

const CELL_CLASSES =
  "text-center flex items-center px-16 py-8 h-48 md:h-56 lg:h-[68px] rounded-8"
const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] line-clamp-2 overflow-hidden text-ellipsis"

type SingleEntryCellDataProps = {
  rowIndex: number
  columnIndex: number
  data: string[][]
  columnCount: number
  tableTheme: "light" | "dark"
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  titleRowIndices: React.MutableRefObject<number[]>
  isButton?: boolean
}

const SingleEntryCellData = ({
  rowIndex,
  columnIndex,
  data,
  columnCount,
  tableTheme,
  backgroundColor,
  titleRowIndices,
  isButton,
}: SingleEntryCellDataProps) => {
  const getProductBySlug = useGetProductBySlug()
  const cellData = data[rowIndex][columnIndex]
  const matches = matchCellPatterns(cellData)
  let price = 0

  const list = parseContent(matches.isList, 6)
  let priceSubScript
  let priceSideScript
  let promotionStartDate
  let promotionEndDate
  let promotionSavings = 0

  if (
    matches.isPriceFromStoryblokSlug ||
    matches.isDetailedPriceFromStoryblokSlug
  ) {
    const slug =
      matches.isDetailedPriceFromStoryblokSlug?.[1] ||
      matches.isPriceFromStoryblokSlug?.[1] ||
      ""

    const product = getProductBySlug(slug)

    if (product) {
      price = Number(product.price)

      if (matches.isDetailedPriceFromStoryblokSlug) {
        const priceData = extractPriceData(product)
        priceSubScript = priceData.priceSubScript
        priceSideScript = priceData.priceSideScript
        promotionSavings = priceData.promotionSavings
        promotionStartDate = priceData.promotionStartDate
        promotionEndDate = priceData.promotionEndDate
      }
    }
  }
  const showPromotion = checkPromotion(promotionStartDate, promotionEndDate)

  let buttonLabel, buttonLink
  if (matches.isButton) {
    const buttonData = extractFromTag(cellData, "button")
    const commaIndex = buttonData.indexOf(",")
    buttonLabel = buttonData.substring(0, commaIndex)
    buttonLink = buttonData.substring(commaIndex + 1)
  }

  return (
    <div
      key={`column-${columnIndex}-row-${rowIndex}`}
      className={classNames(
        CELL_CLASSES,
        "justify-center",
        { "sm:rounded-r-none": columnIndex === 0 },
        {
          "sm:rounded-l-none": columnIndex === columnCount - 1,
        },
        {
          "sm:rounded-none": columnIndex > 0 && columnIndex < columnCount - 1,
        },
        {
          "mt-16 md:mt-24": titleRowIndices.current.find(
            (titleRowIndex) => titleRowIndex === rowIndex
          ),
        },
        tableTheme === "light"
          ? "odd:bg-light-gray"
          : backgroundColor === Color.Charcoal
            ? "odd:bg-black"
            : "odd:bg-charcoal",
        { "mt-10 px-0": isButton || matches.isButton }
      )}
    >
      {matches.isCrossIcon ? (
        <IconCell
          icon={
            <CircleCrossIcon
              className={classNames(
                "shrink-0",
                tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
              )}
            />
          }
          text={matches.isCrossIcon?.[1]}
          theme={tableTheme}
        />
      ) : matches.isCheckIcon ? (
        <IconCell
          icon={
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                getIconFillClass(matches.isCheckIcon?.[1])
              )}
              checkmarkClassName={getCheckmarkStrokeColor(
                matches.isCheckIcon?.[1]
              )}
            />
          }
          text={matches.isCheckIcon?.[2]}
          theme={tableTheme}
        />
      ) : matches.isCheckIconBlackStroke ? (
        <IconCell
          icon={
            <CircleCheckIcon
              width={29}
              height={29}
              showBorder={false}
              opacity={1}
              className={classNames(
                "shrink-0",
                getIconFillClass(matches.isCheckIconBlackStroke?.[1])
              )}
              checkmarkClassName={
                matches.isCheckIconBlackStroke?.[1] === "white"
                  ? "stroke-black"
                  : "stroke-white"
              }
            />
          }
          text={matches.isCheckIconBlackStroke?.[2]}
          theme={tableTheme}
        />
      ) : matches.isDashIcon ? (
        <div className="flex items-center">
          <CircleDashIcon
            width={29}
            height={29}
            showBorder={false}
            opacity={1}
            className={getIconFillClass(matches.isDashIcon?.[1])}
            dashClassName={getDashStrokeColor(matches.isDashIcon?.[1])}
          />
        </div>
      ) : matches.isHeader ? (
        <Typography
          text={extractFromTag(cellData, "header")}
          size="body-lg"
          font="grotesk"
          weight="medium"
          color={getAccentColor(
            tableTheme === "light" ? Color.White : Color.Charcoal
          )}
        />
      ) : cellData.match(/:yocoLogo:/) ? (
        <Logo
          className={classNames(
            "max-h-32",
            tableTheme === "light" ? "fill-black" : "fill-white"
          )}
          alt="Yoco Logo"
        />
      ) : cellData.match(/:crossIcon:/) ? (
        <CircleCrossIcon
          className={
            tableTheme === "light" ? "stroke-charcoal" : "stroke-white"
          }
        />
      ) : matches.isList ? (
        <div className="flex flex-col gap-0.5">
          {list.map((listItem, index) => (
            <div className="flex gap-2" key={index}>
              <CircleCheckIcon
                height={12}
                className={
                  tableTheme === "light" ? "text-charcoal" : "text-white"
                }
              />
              <Typography
                text={listItem}
                size="subscript-sm"
                font="grotesk"
                weight="book"
                color={getAccentColor(
                  tableTheme === "light" ? Color.White : Color.Charcoal
                )}
              />
            </div>
          ))}
        </div>
      ) : matches.isPriceFromStoryblokSlug ? (
        <Typography
          font="grotesk"
          weight="medium"
          color={getThemeColor(tableTheme)}
          size="lead-lg"
          text={"R" + price}
          className={CELL_TEXT_CLASSSES}
        />
      ) : matches.isDetailedPriceFromStoryblokSlug ? (
        <div className="flex flex-col items-start gap-0.5">
          {showPromotion && promotionSavings > 0 ? (
            <Typography
              font="grotesk"
              weight="book"
              color={getThemeColor(tableTheme)}
              size="subscript-sm"
              text={"SAVE R" + promotionSavings}
              className={CELL_TEXT_CLASSSES + " opacity-40"}
              leadingNone
            />
          ) : null}
          <div className="flex place-items-baseline gap-2">
            <Typography
              font="grotesk"
              weight="medium"
              color={getThemeColor(tableTheme)}
              size="lead-sm"
              text={"R" + price}
              className={CELL_TEXT_CLASSSES}
              leadingNone
            />
            <Typography
              font="grotesk"
              weight="medium"
              color={getThemeColor(tableTheme)}
              size="subscript-sm"
              text={priceSideScript ? priceSideScript : ""}
              className={CELL_TEXT_CLASSSES}
              leadingNone
            />
          </div>
          <Typography
            font="grotesk"
            weight="book"
            color={getThemeColor(tableTheme)}
            size="subscript-sm"
            text={priceSubScript ? priceSubScript : ""}
            className={CELL_TEXT_CLASSSES + " text-nowrap"}
            leadingNone
          />
        </div>
      ) : matches.isButton && buttonLabel && buttonLink ? (
        <PillButton
          size="medium"
          style="solid"
          text={buttonLabel}
          color={Color.Blue}
          linkUrl={buttonLink}
          trackingEvent="TableButtonEvent"
          trackingEventKey="column"
          trackingEventValue={data[0][columnIndex]}
        />
      ) : (
        <Tooltip
          text={matches.isPrice ? extractFromTag(cellData, "price") : cellData}
          backgroundColor={getThemeColor(tableTheme)}
          showTooltipCondition="hoverContentEllipsed"
        >
          <Typography
            font="grotesk"
            weight={matches.isPrice || rowIndex === 0 ? "medium" : "book"}
            color={getThemeColor(tableTheme)}
            size={
              matches.isPrice
                ? "lead-lg"
                : rowIndex === 0
                  ? "body-lg"
                  : "body-md"
            }
            text={
              matches.isPrice ? extractFromTag(cellData, "price") : cellData
            }
            className={CELL_TEXT_CLASSSES}
          />
        </Tooltip>
      )}
    </div>
  )
}

export default SingleEntryCellData
